import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Plus,
  Trash2,
  Pencil,
  LogOut,
  X,
  Upload,
  BarChart3,
  Inbox,
  UtensilsCrossed,
  Users,
  Camera,
  Instagram,
  Newspaper,
  Smartphone,
} from "lucide-react";

const emptyBrand = {
  name: "",
  cuisine: "",
  description: "",
  color: "#C8A97E",
  image: "",
  logo_letter: "",
  tagline: "",
  is_coming_soon: false,
  is_halal: false,
  is_opening_soon: false,
  just_eat_url: "",
  deliveroo_url: "",
  uber_eats_url: "",
  panda_url: "",
  direct_order_url: "",
  instagram_url: "",
  facebook_url: "",
  tiktok_url: "",
  website_url: "",
  menu_pdf_url: "",
  order: 0,
};

const TABS = [
  { key: "brands", label: "Brands", icon: UtensilsCrossed },
  { key: "kitchen-tour", label: "Kitchen Tour", icon: Camera },
  { key: "social-posts", label: "Social Feed", icon: Instagram },
  { key: "news", label: "News", icon: Newspaper },
  { key: "apps", label: "Apps", icon: Smartphone },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "enquiries", label: "Partner Enquiries", icon: Inbox },
  { key: "subscribers", label: "Club Members", icon: Users },
];

const resolveUrl = (url) => {
  if (!url) return "";
  // Relative /api/uploads paths just work directly from the current origin
  return url;
};

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("brands");
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = async () => {
    await api.post("/auth/logout");
    nav("/admin/login");
  };

  if (!user) {
    return (
      <main className="min-h-screen flex items-center justify-center text-muted font-mono text-sm">
        Loading…
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="admin-panel">
      <header className="border-b border-[var(--border)] sticky top-0 bg-[var(--bg)]/95 backdrop-blur z-40">
        <div className="container-nk h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/naami-wordmark.png"
              alt="Naami Kitchens United"
              className="h-7 w-auto"
              style={{ filter: "invert(1) brightness(1.05)" }}
            />
            <div className="border-l border-[var(--border)] pl-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted">
                Admin · {user.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="btn-ghost" style={{ padding: "10px 18px", fontSize: "11px" }}>
              View Site
            </a>
            <button
              onClick={logout}
              className="btn-ghost"
              style={{ padding: "10px 18px", fontSize: "11px" }}
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="container-nk flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "text-gold border-[var(--gold)]"
                    : "text-[var(--text)]/75 border-transparent hover:text-[var(--text)] hover:border-[var(--border)]"
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="container-nk py-8">
        {tab === "brands" && <BrandsTab />}
        {tab === "kitchen-tour" && <KitchenTourTab />}
        {tab === "social-posts" && <SocialPostsTab />}
        {tab === "news" && <NewsTab />}
        {tab === "apps" && <AppsTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "subscribers" && <SubscribersTab />}
      </div>
    </main>
  );
}

/* ============== BRANDS TAB ============== */
function BrandsTab() {
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBrand);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);

  const load = () => api.get("/brands").then((r) => setBrands(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (b) => { setEditing(b.id); setForm({ ...emptyBrand, ...b }); };
  const startCreate = () => { setEditing("new"); setForm({ ...emptyBrand, order: brands.length }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyBrand); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/brands", payload);
      } else {
        await api.put(`/admin/brands/${editing}`, payload);
      }
      await load();
      cancelEdit();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.name}?`)) return;
    await api.delete(`/admin/brands/${b.id}`);
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: res.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, menu_pdf_url: res.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploadingPdf(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };


  return (
    <>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow">// Brand CMS</p>
          <h1 className="font-display text-[44px] leading-none mt-2">Manage Brands</h1>
          <p className="text-muted text-[13.5px] mt-2">
            {brands.length} brand{brands.length !== 1 ? "s" : ""} live
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="admin-add-brand">
          <Plus size={14} /> Add Brand
        </button>
      </div>

      <div className="grid gap-3" data-testid="admin-brand-list">
        {brands.map((b) => (
          <div
            key={b.id}
            className="flex items-center gap-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4"
            data-testid={`admin-row-${b.name.replace(/\s+/g, "-")}`}
          >
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center font-display text-[16px]"
              style={{ background: `${b.color}22`, border: `1px solid ${b.color}55`, color: b.color }}
            >
              {b.logo_letter || b.name.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-[20px] leading-tight">{b.name}</p>
                {b.is_halal && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[rgba(22,163,74,0.18)] text-[#4ade80] border border-[rgba(74,222,128,0.35)]">
                    Halal
                  </span>
                )}
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted truncate">
                {b.cuisine} {b.is_coming_soon && "· Coming Soon"}
              </p>
            </div>
            <button
              onClick={() => startEdit(b)}
              className="btn-ghost"
              style={{ padding: "8px 14px", fontSize: "11px" }}
              data-testid={`admin-edit-${b.name.replace(/\s+/g, "-")}`}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              onClick={() => remove(b)}
              className="btn-ghost"
              style={{ padding: "8px 14px", fontSize: "11px", borderColor: "rgba(224,32,32,0.4)", color: "#ff7070" }}
              data-testid={`admin-delete-${b.name.replace(/\s+/g, "-")}`}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center p-4"
          onClick={cancelEdit}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="w-full max-w-[720px] max-h-[92vh] overflow-y-auto bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl p-7"
            data-testid="admin-edit-form"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-[32px] leading-none">
                {editing === "new" ? "New Brand" : "Edit Brand"}
              </h2>
              <button type="button" onClick={cancelEdit} className="text-muted hover:text-[var(--text)]" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {/* Image upload */}
            <div className="mb-6">
              <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-2">
                Brand photo
              </label>
              <div className="flex items-start gap-4">
                <div
                  className="w-[140px] h-[90px] rounded-lg border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="admin-photo-preview"
                >
                  {form.image ? (
                    <img src={resolveUrl(form.image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-muted text-[11px] font-mono">No photo</span>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={upload}
                    className="hidden"
                    data-testid="admin-photo-file"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="btn-ghost justify-center"
                    style={{ padding: "10px 16px", fontSize: "11px" }}
                    data-testid="admin-photo-upload-btn"
                  >
                    <Upload size={13} /> {uploading ? "Uploading…" : "Upload from device"}
                  </button>
                  <input
                    placeholder="or paste image URL"
                    className="input-nk"
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    data-testid="admin-photo-url"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["name", "Brand name"],
                ["cuisine", "Cuisine type"],
                ["color", "Color hex (#RRGGBB)"],
                ["logo_letter", "Logo letters (2-3 chars)"],
                ["tagline", "Tagline (optional)"],
                ["order", "Display order"],
              ].map(([key, label]) => (
                <div key={key} className={key === "tagline" ? "md:col-span-2" : ""}>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
                    {label}
                  </label>
                  <input
                    className="input-nk"
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
                  Description (2-3 sentences)
                </label>
                <textarea
                  className="input-nk"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="coming-soon"
                    checked={form.is_coming_soon}
                    onChange={(e) => setForm({ ...form, is_coming_soon: e.target.checked })}
                  />
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
                    Coming Soon (ghost card)
                  </span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="is-halal"
                    checked={form.is_halal}
                    onChange={(e) => setForm({ ...form, is_halal: e.target.checked })}
                    data-testid="admin-halal-toggle"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[#4ade80]">
                    Halal · shows badge on card
                  </span>
                </label>

                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="is-opening-soon"
                    checked={form.is_opening_soon}
                    onChange={(e) => setForm({ ...form, is_opening_soon: e.target.checked })}
                    data-testid="admin-opening-soon-toggle"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gold">
                    Opening Soon · stamp on logo
                  </span>
                </label>
              </div>

              <div className="md:col-span-2 pt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mb-3">
                  Ordering URLs
                </p>
              </div>
              {[
                ["just_eat_url", "Just Eat URL"],
                ["deliveroo_url", "Deliveroo URL"],
                ["uber_eats_url", "Uber Eats URL"],
                ["panda_url", "Panda URL"],
                ["direct_order_url", "Direct Order URL (brand's own app)"],
                ["website_url", "Brand Website URL"],
                ["instagram_url", "Instagram URL"],
                ["facebook_url", "Facebook URL"],
                ["tiktok_url", "TikTok URL"],
              ].map(([key, label]) => (
                <div key={key} className={key === "direct_order_url" ? "md:col-span-2" : ""}>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
                    {label}
                  </label>
                  <input
                    className="input-nk"
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder="https://"
                  />
                </div>
              ))}
            </div>

            {/* Menu PDF uploader */}
            <div className="mt-6 pt-6 border-t border-[var(--border)]">
              <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
                Menu PDF (optional)
              </label>
              <p className="text-[12px] text-muted mb-3">
                Upload a PDF menu. A "View Menu" button will appear on this brand's card.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  className="input-nk flex-1 min-w-[260px]"
                  value={form.menu_pdf_url ?? ""}
                  onChange={(e) => setForm({ ...form, menu_pdf_url: e.target.value })}
                  placeholder="PDF URL or upload below"
                />
                <button
                  type="button"
                  onClick={() => pdfRef.current?.click()}
                  className="btn-ghost"
                  disabled={uploadingPdf}
                  data-testid="admin-pdf-upload"
                >
                  <Upload size={13} /> {uploadingPdf ? "Uploading…" : "Upload PDF"}
                </button>
                <input
                  ref={pdfRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={uploadPdf}
                  className="hidden"
                />
                {form.menu_pdf_url && (
                  <a
                    href={form.menu_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    data-testid="admin-pdf-preview"
                  >
                    Preview PDF →
                  </a>
                )}
                {form.menu_pdf_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, menu_pdf_url: "" })}
                    className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-[var(--error)] transition-colors"
                    data-testid="admin-pdf-clear"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-7 justify-end">
              <button type="button" onClick={cancelEdit} className="btn-ghost" data-testid="admin-cancel-edit">
                Cancel
              </button>
              <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-save-brand">
                {busy ? "Saving…" : "Save Brand"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/* ============== ANALYTICS TAB ============== */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-muted font-mono text-sm">Loading analytics…</p>;
  if (!data) return <p className="text-muted">Couldn't load analytics.</p>;

  const max = Math.max(1, ...data.brands_ranked.map((b) => b.total));

  return (
    <div data-testid="admin-analytics">
      <div className="mb-8">
        <p className="eyebrow">// Last 30 days</p>
        <h1 className="font-display text-[44px] leading-none mt-2">Analytics</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatTile label="Total clicks" value={data.total_events} />
        <StatTile label="Club members" value={data.total_subscribers} />
        <StatTile label="New enquiries" value={data.new_partner_enquiries} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="font-display text-[24px] mb-5">Most popular brands</h3>
          {data.brands_ranked.length === 0 && (
            <p className="text-muted text-[13px]">
              No clicks tracked yet. Open the site, click some platform buttons, and refresh.
            </p>
          )}
          <div className="flex flex-col gap-3">
            {data.brands_ranked.slice(0, 12).map((b, i) => (
              <div key={b.brand_name} className="flex items-center gap-3" data-testid={`analytics-brand-${i}`}>
                <span className="font-display text-[20px] text-muted w-7">{(i + 1).toString().padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-3 mb-1">
                    <span className="font-display text-[18px] truncate">{b.brand_name}</span>
                    <span className="font-mono text-[12px] text-gold">{b.total}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[var(--surface-elevated)] overflow-hidden">
                    <div
                      className="h-full bg-[var(--gold)] rounded-full transition-all"
                      style={{ width: `${(b.total / max) * 100}%` }}
                    />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted mt-1.5">
                    Platform: {b.platform_clicks} · Website: {b.website_clicks} · Social: {b.social_clicks}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="font-display text-[24px] mb-5">Top platforms</h3>
          {data.platforms.length === 0 ? (
            <p className="text-muted text-[13px]">No platform clicks yet.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {data.platforms.map((p) => (
                <div key={p.platform} className="flex items-center justify-between border-b border-[var(--border)] pb-2 last:border-0">
                  <span className="font-display text-[18px]">{p.platform}</span>
                  <span className="font-mono text-[14px] text-gold">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">{label}</p>
      <p className="font-display text-[56px] leading-none text-gold mt-2">{value}</p>
    </div>
  );
}

/* ============== ENQUIRIES TAB ============== */
function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/partner-enquiries").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/partner-enquiries/${id}`);
    load();
  };

  if (loading) return <p className="text-muted font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-enquiries">
      <div className="mb-8">
        <p className="eyebrow">// Inbox</p>
        <h1 className="font-display text-[44px] leading-none mt-2">Partner Enquiries</h1>
        <p className="text-muted text-[13.5px] mt-2">{items.length} total</p>
      </div>

      {items.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-10 text-center">
          <p className="text-muted text-[14px]">No enquiries yet. Submissions from the "Partner With Us" form will land here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((e) => (
            <div key={e.id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-display text-[22px] leading-none">{e.brand_name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mt-1">
                    {e.cuisine || "—"} · {new Date(e.created_at).toLocaleString("en-GB")}
                  </p>
                </div>
                <button
                  onClick={() => remove(e.id)}
                  className="btn-ghost"
                  style={{ padding: "6px 12px", fontSize: "10px", borderColor: "rgba(224,32,32,0.4)", color: "#ff7070" }}
                  data-testid={`admin-delete-enquiry-${e.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] mb-3">
                <span><span className="text-muted">Name:</span> {e.name}</span>
                <span><span className="text-muted">Email:</span> <a href={`mailto:${e.email}`} className="text-gold hover:underline">{e.email}</a></span>
                {e.phone && <span><span className="text-muted">Phone:</span> {e.phone}</span>}
              </div>
              <p className="text-[14px] leading-relaxed text-[var(--text)]/85 whitespace-pre-wrap">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============== SUBSCRIBERS TAB ============== */
function SubscribersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/subscribers").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    load();
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Phone", "Email", "Address", "Voucher code", "Redeemed", "Referred by", "Referrals made", "Bonus codes", "Signed up"],
      ...items.map((i) => [
        i.name,
        i.phone,
        i.email,
        i.address,
        i.voucher_code,
        i.voucher_used ? "yes" : "no",
        i.referred_by || "",
        i.referral_count || 0,
        (i.bonus_codes || []).join(" "),
        i.created_at,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `naami-club-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const toggleRedeem = async (s) => {
    const path = s.voucher_used ? "unredeem" : "redeem";
    await api.post(`/admin/subscribers/${s.id}/${path}`);
    load();
  };

  if (loading) return <p className="text-muted font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow">// Naami Club</p>
          <h1 className="font-display text-[44px] leading-none mt-2">Club Members</h1>
          <p className="text-muted text-[13.5px] mt-2">{items.length} total</p>
        </div>
        {items.length > 0 && (
          <button onClick={exportCsv} className="btn-primary" data-testid="admin-export-csv">
            Export CSV
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-10 text-center">
          <p className="text-muted text-[14px]">No members yet. Sign-ups from the "Join the Club" form will land here.</p>
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[var(--surface-elevated)] font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Phone</th>
                <th className="px-5 py-3 hidden md:table-cell">Email</th>
                <th className="px-5 py-3">30% Voucher</th>
                <th className="px-5 py-3 hidden lg:table-cell">Referrals</th>
                <th className="px-5 py-3 hidden lg:table-cell">Signed up</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-[var(--border)] text-[13px]" data-testid={`admin-sub-${s.id}`}>
                  <td className="px-5 py-3 font-display text-[16px]">{s.name}</td>
                  <td className="px-5 py-3">{s.phone}</td>
                  <td className="px-5 py-3 text-muted hidden md:table-cell">{s.email || "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`font-mono text-[12px] tracking-wider px-2 py-1 rounded ${
                          s.voucher_used
                            ? "bg-[var(--surface-elevated)] text-muted line-through"
                            : "bg-[var(--gold)]/15 text-gold"
                        }`}
                      >
                        {s.voucher_code || "—"}
                      </span>
                      {s.voucher_code && (
                        <button
                          onClick={() => toggleRedeem(s)}
                          className={`font-mono text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded border transition-colors ${
                            s.voucher_used
                              ? "border-[var(--border)] text-muted hover:text-[var(--text)]"
                              : "border-[var(--gold)]/50 text-gold hover:bg-[var(--gold)]/10"
                          }`}
                          data-testid={`admin-redeem-${s.id}`}
                        >
                          {s.voucher_used ? "Unredeem" : "Mark redeemed"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono text-[12px]">
                        {s.referral_count || 0} <span className="text-muted">referred</span>
                      </span>
                      {s.referred_by && (
                        <span className="font-mono text-[10px] text-muted">
                          via {s.referred_by}
                        </span>
                      )}
                      {(s.bonus_codes || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(s.bonus_codes || []).map((bc) => (
                            <span key={bc} className="font-mono text-[10px] tracking-wider px-1.5 py-0.5 rounded bg-[var(--gold)]/15 text-gold">
                              +{bc}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-muted hidden lg:table-cell">
                    {new Date(s.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => remove(s.id)}
                      className="text-muted hover:text-[var(--error)] transition-colors"
                      aria-label="Delete subscriber"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== KITCHEN TOUR TAB ============== */
function KitchenTourTab() {
  const [photos, setPhotos] = useState([]);
  const [editing, setEditing] = useState(null);
  const emptyPhoto = { image: "", label: "", sub: "", order: 0 };
  const [form, setForm] = useState(emptyPhoto);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/kitchen-tour").then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ ...emptyPhoto, order: photos.length }); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...emptyPhoto, ...p }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyPhoto); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/kitchen-tour", payload);
      } else {
        await api.put(`/admin/kitchen-tour/${editing}`, payload);
      }
      await load();
      cancelEdit();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.label}"?`)) return;
    await api.delete(`/admin/kitchen-tour/${p.id}`);
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: res.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (editing) {
    return (
      <div data-testid="admin-kitchen-tour-edit">
        <p className="eyebrow text-gold">// {editing === "new" ? "New Photo" : "Edit Photo"}</p>
        <h2 className="font-display text-[40px] leading-none mt-2 mb-7">
          {editing === "new" ? "Add Kitchen Photo" : "Edit Photo"}
        </h2>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Image
            </label>
            <div className="flex items-center gap-3">
              <input
                className="input-nk flex-1"
                placeholder="Image URL or upload"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-ghost"
                disabled={uploading}
                data-testid="admin-kitchen-upload"
              >
                <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            </div>
            {form.image && (
              <img src={form.image} alt="" className="mt-3 rounded-lg max-h-[240px] border border-[var(--border)]" />
            )}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Label *
            </label>
            <input
              required
              className="input-nk"
              placeholder="e.g. The Walk-In Counter"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Order
            </label>
            <input
              type="number"
              className="input-nk"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Caption / Sub
            </label>
            <input
              className="input-nk"
              placeholder="One short line of context"
              value={form.sub}
              onChange={(e) => setForm({ ...form, sub: e.target.value })}
            />
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end pt-2">
            <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-kitchen-save">
              {busy ? "Saving…" : "Save Photo"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-kitchen-tour-list">
      <div className="flex items-end justify-between mb-7">
        <div>
          <p className="eyebrow text-gold">// Kitchen Tour</p>
          <h2 className="font-display text-[40px] leading-none mt-2">Manage Kitchen Photos</h2>
          <p className="text-muted text-[13px] mt-2">{photos.length} photos shown in the homepage gallery</p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="admin-add-kitchen-photo">
          <Plus size={14} /> Add Photo
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {photos.map((p) => (
          <div key={p.id} className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--surface)]">
            <div className="aspect-[16/10] bg-[var(--surface-elevated)] overflow-hidden">
              {p.image && <img src={p.image} alt={p.label} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display text-[20px] leading-tight truncate">{p.label}</p>
                  {p.sub && <p className="text-[12px] text-muted mt-1 line-clamp-2">{p.sub}</p>}
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mt-2">Order: {p.order}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(p)} className="p-2 text-muted hover:text-gold transition-colors" aria-label="Edit"><Pencil size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 text-muted hover:text-[var(--error)] transition-colors" aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== SOCIAL POSTS TAB ============== */
function SocialPostsTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const emptyPost = { brand_name: "", image: "", caption: "", permalink: "#" };
  const [form, setForm] = useState(emptyPost);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/social-posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm(emptyPost); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...emptyPost, ...p }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyPost); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/social-posts", form);
      } else {
        await api.put(`/admin/social-posts/${editing}`, form);
      }
      await load();
      cancelEdit();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete this post by ${p.brand_name}?`)) return;
    await api.delete(`/admin/social-posts/${p.id}`);
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: res.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (editing) {
    return (
      <div data-testid="admin-social-edit">
        <p className="eyebrow text-gold">// {editing === "new" ? "New Post" : "Edit Post"}</p>
        <h2 className="font-display text-[40px] leading-none mt-2 mb-7">
          {editing === "new" ? "Add Social Post" : "Edit Social Post"}
        </h2>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Image
            </label>
            <div className="flex items-center gap-3">
              <input
                className="input-nk flex-1"
                placeholder="Image URL or upload"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-ghost"
                disabled={uploading}
                data-testid="admin-social-upload"
              >
                <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            </div>
            {form.image && (
              <img src={form.image} alt="" className="mt-3 rounded-lg max-h-[240px] border border-[var(--border)]" />
            )}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Brand name *
            </label>
            <input
              required
              className="input-nk"
              placeholder="e.g. 2Bros"
              value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Link / Permalink
            </label>
            <input
              className="input-nk"
              placeholder="https://instagram.com/p/…"
              value={form.permalink}
              onChange={(e) => setForm({ ...form, permalink: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Caption *
            </label>
            <textarea
              required
              rows={3}
              className="input-nk"
              placeholder="What's the story?"
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
            />
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end pt-2">
            <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-social-save">
              {busy ? "Saving…" : "Save Post"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-social-list">
      <div className="flex items-end justify-between mb-7">
        <div>
          <p className="eyebrow text-gold">// Social Feed</p>
          <h2 className="font-display text-[40px] leading-none mt-2">Manage Social Posts</h2>
          <p className="text-muted text-[13px] mt-2">{posts.length} posts shown in "Fresh from the Counter"</p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="admin-add-social-post">
          <Plus size={14} /> Add Post
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((p) => (
          <div key={p.id} className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--surface)]">
            <div className="aspect-square bg-[var(--surface-elevated)] overflow-hidden">
              {p.image && <img src={p.image} alt={p.caption} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-gold">@{p.brand_name.toLowerCase().replace(/[^a-z0-9]+/g, "")}</p>
                  <p className="text-[13px] mt-2 line-clamp-3 text-[var(--text)]/85">{p.caption}</p>
                  <p className="font-mono text-[10px] text-muted mt-2">{new Date(p.posted_at).toLocaleDateString("en-GB")}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(p)} className="p-2 text-muted hover:text-gold transition-colors" aria-label="Edit"><Pencil size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 text-muted hover:text-[var(--error)] transition-colors" aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== NEWS TAB ============== */
function NewsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const emptyItem = {
    headline: "",
    blurb: "",
    category: "",
    image: "",
    link: "",
    is_breaking: true,
    order: 0,
    published_at: "",
  };
  const [form, setForm] = useState(emptyItem);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ ...emptyItem, order: items.length }); };
  const startEdit = (n) => { setEditing(n.id); setForm({ ...emptyItem, ...n }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyItem); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (!payload.published_at) delete payload.published_at;
      if (editing === "new") {
        await api.post("/admin/news", payload);
      } else {
        await api.put(`/admin/news/${editing}`, payload);
      }
      await load();
      cancelEdit();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.headline}"?`)) return;
    await api.delete(`/admin/news/${n.id}`);
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: res } = await api.post("/admin/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: res.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (editing) {
    return (
      <div data-testid="admin-news-edit">
        <p className="eyebrow text-gold">// {editing === "new" ? "New Update" : "Edit Update"}</p>
        <h2 className="font-display text-[40px] leading-none mt-2 mb-7">
          {editing === "new" ? "Add News Update" : "Edit News Update"}
        </h2>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Headline *
            </label>
            <input
              required
              className="input-nk"
              placeholder="e.g. New brand 'Bao Lab' opens this Saturday"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Blurb / short description
            </label>
            <textarea
              rows={3}
              className="input-nk"
              placeholder="A couple of lines about this update."
              value={form.blurb}
              onChange={(e) => setForm({ ...form, blurb: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Category badge
            </label>
            <input
              className="input-nk"
              placeholder="e.g. NEW BRAND · OFFER · PRESS"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              External link (optional)
            </label>
            <input
              className="input-nk"
              placeholder="https://…"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Image (optional)
            </label>
            <div className="flex items-center gap-3">
              <input
                className="input-nk flex-1"
                placeholder="Image URL or upload"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-ghost"
                disabled={uploading}
                data-testid="admin-news-upload"
              >
                <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            </div>
            {form.image && (
              <img src={form.image} alt="" className="mt-3 rounded-lg max-h-[240px] border border-[var(--border)]" />
            )}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Order
            </label>
            <input
              type="number"
              className="input-nk"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_breaking}
                onChange={(e) => setForm({ ...form, is_breaking: e.target.checked })}
                data-testid="admin-news-breaking"
              />
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gold">
                Show in top scrolling ticker
              </span>
            </label>
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end pt-2">
            <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-news-save">
              {busy ? "Saving…" : "Save Update"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-news-list">
      <div className="flex items-end justify-between mb-7">
        <div>
          <p className="eyebrow text-gold">// News &amp; Updates</p>
          <h2 className="font-display text-[40px] leading-none mt-2">Manage News</h2>
          <p className="text-muted text-[13px] mt-2">
            {items.length} item{items.length === 1 ? "" : "s"} ·{" "}
            {items.filter((i) => i.is_breaking).length} shown in the scrolling ticker
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="admin-add-news">
          <Plus size={14} /> Add Update
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((n) => (
          <div key={n.id} className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--surface)]">
            <div className="flex">
              {n.image && (
                <div className="w-32 flex-shrink-0 bg-[var(--surface-elevated)]">
                  <img src={n.image} alt={n.headline} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  {n.category && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--bg)] bg-[var(--gold)] px-2 py-0.5 rounded">
                      {n.category}
                    </span>
                  )}
                  {n.is_breaking && (
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold">● Ticker</span>
                  )}
                </div>
                <p className="font-display text-[18px] leading-tight line-clamp-2">{n.headline}</p>
                {n.blurb && (
                  <p className="text-[12px] text-muted mt-1.5 line-clamp-2">{n.blurb}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <p className="font-mono text-[10px] text-muted">
                    {new Date(n.published_at).toLocaleDateString("en-GB")} · ord {n.order}
                  </p>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(n)} className="p-1.5 text-muted hover:text-gold transition-colors" aria-label="Edit"><Pencil size={13} /></button>
                    <button onClick={() => remove(n)} className="p-1.5 text-muted hover:text-[var(--error)] transition-colors" aria-label="Delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============== APPS TAB ============== */
const APP_ICON_OPTIONS = [
  { key: "truck", label: "Truck (driver)" },
  { key: "radio", label: "Radio (dispatch)" },
  { key: "smartphone", label: "Smartphone" },
  { key: "shield", label: "Shield (admin)" },
  { key: "utensils", label: "Utensils (kitchen)" },
  { key: "users", label: "Users / Team" },
  { key: "wrench", label: "Wrench (ops)" },
  { key: "boxes", label: "Boxes (inventory)" },
];

const APP_COLOUR_OPTIONS = [
  "#C8A97E", "#4ade80", "#60a5fa", "#f59e0b", "#ef4444",
  "#a855f7", "#ec4899", "#14b8a6", "#f97316", "#84cc16",
];

function AppsTab() {
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null);
  const emptyApp = {
    name: "",
    audience: "",
    description: "",
    url: "",
    icon: "truck",
    color: "#C8A97E",
    order: 0,
  };
  const [form, setForm] = useState(emptyApp);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/apps").then((r) => setApps(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ ...emptyApp, order: apps.length }); };
  const startEdit = (a) => { setEditing(a.id); setForm({ ...emptyApp, ...a }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyApp); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/apps", payload);
      } else {
        await api.put(`/admin/apps/${editing}`, payload);
      }
      await load();
      cancelEdit();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    await api.delete(`/admin/apps/${a.id}`);
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-apps-edit">
        <p className="eyebrow text-gold">// {editing === "new" ? "New App" : "Edit App"}</p>
        <h2 className="font-display text-[40px] leading-none mt-2 mb-7">
          {editing === "new" ? "Add Internal App" : "Edit Internal App"}
        </h2>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-5 max-w-3xl">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              App name *
            </label>
            <input
              required
              className="input-nk"
              placeholder="e.g. Naami Driver"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Audience badge *
            </label>
            <input
              required
              className="input-nk"
              placeholder="e.g. For Delivery Drivers"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Description *
            </label>
            <textarea
              required
              rows={3}
              className="input-nk"
              placeholder="Short paragraph describing what the app does."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              App URL *
            </label>
            <input
              required
              className="input-nk"
              placeholder="https://…"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Icon
            </label>
            <select
              className="input-nk"
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
            >
              {APP_ICON_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Accent colour
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {APP_COLOUR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className="w-8 h-8 rounded-full border-2"
                  style={{
                    background: c,
                    borderColor: form.color === c ? "var(--text)" : "transparent",
                  }}
                  aria-label={`Pick ${c}`}
                />
              ))}
              <input
                className="input-nk flex-1 min-w-[120px]"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#C8A97E"
              />
            </div>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-1.5">
              Order
            </label>
            <input
              type="number"
              className="input-nk"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </div>
          <div className="md:col-span-2 flex gap-3 justify-end pt-2">
            <button type="button" onClick={cancelEdit} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-app-save">
              {busy ? "Saving…" : "Save App"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-apps-list">
      <div className="flex items-end justify-between mb-7">
        <div>
          <p className="eyebrow text-gold">// Internal Apps</p>
          <h2 className="font-display text-[40px] leading-none mt-2">Manage Apps</h2>
          <p className="text-muted text-[13px] mt-2">
            {apps.length} app{apps.length === 1 ? "" : "s"} · visible at{" "}
            <code className="text-gold">/apps</code> (not linked from main nav)
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="admin-add-app">
          <Plus size={14} /> Add App
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {apps.map((a) => (
          <div key={a.id} className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-5 flex gap-4">
            <div
              className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center font-display text-[20px]"
              style={{
                background: `color-mix(in srgb, ${a.color} 18%, transparent)`,
                border: `1px solid color-mix(in srgb, ${a.color} 45%, transparent)`,
                color: a.color,
              }}
              aria-hidden
            >
              {a.icon === "truck" && "🚚"}
              {a.icon === "radio" && "📡"}
              {a.icon === "smartphone" && "📱"}
              {a.icon === "shield" && "🛡️"}
              {a.icon === "utensils" && "🍴"}
              {a.icon === "users" && "👥"}
              {a.icon === "wrench" && "🔧"}
              {a.icon === "boxes" && "📦"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.14em]" style={{ color: a.color }}>
                {a.audience}
              </p>
              <p className="font-display text-[20px] leading-tight mt-1">{a.name}</p>
              <p className="text-[12px] text-muted mt-1.5 line-clamp-2">{a.description}</p>
              <p className="font-mono text-[10px] text-muted mt-2 truncate">{a.url}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="font-mono text-[10px] text-muted">ord {a.order}</span>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(a)} className="p-1.5 text-muted hover:text-gold transition-colors" aria-label="Edit"><Pencil size={13} /></button>
                  <button onClick={() => remove(a)} className="p-1.5 text-muted hover:text-[var(--error)] transition-colors" aria-label="Delete"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

